import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fdate, ROLES } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, TableWrap, Th, Td, Field, NSelect } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const EMPTY = { name: "", email: "", password: "", role: "commercial", active: true };

export default function Users() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/users").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (edit.id) {
        const body = { name: edit.name, role: edit.role, active: edit.active, ...(edit.password && { password: edit.password }) };
        await api.put(`/users/${edit.id}`, body);
      } else {
        await api.post("/users", { name: edit.name, email: edit.email, password: edit.password, role: edit.role });
      }
      toast.success("Utilisateur enregistré"); setEdit(null); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async (u) => {
    if (!window.confirm(`Supprimer le compte de ${u.name} ?`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("Compte supprimé"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <PageHeader kicker="Administration" title="Utilisateurs & rôles">
        <Button className="bg-red-600 hover:bg-red-700" onClick={() => setEdit(EMPTY)} data-testid="new-user-btn"><Plus className="mr-1 h-4 w-4" /> Nouveau compte</Button>
      </PageHeader>
      <TableWrap>
        <thead><tr><Th>Nom</Th><Th>Email</Th><Th>Rôle</Th><Th>Créé le</Th><Th>État</Th><Th className="text-right">Actions</Th></tr></thead>
        <tbody>
          {items.map((u) => (
            <tr key={u.id} data-testid={`user-row-${u.email}`} className="hover:bg-slate-50">
              <Td className="font-medium">{u.name}</Td>
              <Td className="text-slate-600">{u.email}</Td>
              <Td><span className="text-xs font-semibold uppercase tracking-wider text-blue-900">{ROLES[u.role]}</span></Td>
              <Td>{fdate(u.created_at)}</Td>
              <Td>{u.active !== false ? <span className="text-xs text-emerald-600">Actif</span> : <span className="text-xs text-slate-400">Désactivé</span>}</Td>
              <Td className="text-right">
                <div className="flex justify-end gap-1.5">
                  <Button size="sm" variant="outline" onClick={() => setEdit({ ...u, password: "" })} data-testid={`user-edit-${u.email}`}><Pencil className="h-3.5 w-3.5" /></Button>
                  {u.id !== user.id && <Button size="sm" variant="outline" onClick={() => remove(u)} data-testid={`user-delete-${u.email}`}><Trash2 className="h-3.5 w-3.5" /></Button>}
                </div>
              </Td>
            </tr>
          ))}
        </tbody>
      </TableWrap>
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent data-testid="user-dialog">
          <DialogHeader><DialogTitle>{edit?.id ? "Modifier le compte" : "Nouveau compte"}</DialogTitle></DialogHeader>
          {edit && (
            <div className="grid gap-3">
              <Field label="Nom complet *"><Input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} data-testid="user-name-input" /></Field>
              <Field label="Email *"><Input type="email" disabled={!!edit.id} value={edit.email} onChange={(e) => setEdit({ ...edit, email: e.target.value })} data-testid="user-email-input" /></Field>
              <Field label={edit.id ? "Nouveau mot de passe (laisser vide pour conserver)" : "Mot de passe * (6 caractères min.)"}>
                <Input type="password" value={edit.password} onChange={(e) => setEdit({ ...edit, password: e.target.value })} data-testid="user-password-input" />
              </Field>
              <Field label="Rôle">
                <NSelect value={edit.role} onChange={(v) => setEdit({ ...edit, role: v })} testId="user-role-select">
                  {Object.entries(ROLES).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </NSelect>
              </Field>
              {edit.id && edit.id !== user.id && (
                <label className="flex items-center gap-2 text-sm"><Switch checked={edit.active !== false} onCheckedChange={(v) => setEdit({ ...edit, active: v })} data-testid="user-active-switch" /> Compte actif</label>
              )}
            </div>
          )}
          <DialogFooter><Button className="bg-red-600 hover:bg-red-700" onClick={save} data-testid="user-save-btn">Enregistrer</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
